/**
 * vehicleMappingStore — INTERIM localStorage persistence for the
 * Atlas → Ops vehicle mapping.
 *
 * ⚠️ INTERIM STORE (Phase 1, PRD §7.2 / §8.4-D). This persists the mapping
 * `cartographerVehicleId | slotId → opsVehicleId` to `localStorage`, keyed by
 * `${environment}:${networkId}`, so the mapping survives a page reload. It is a
 * deliberate, *justified* local store because Ops has no concept of *our*
 * Atlas vehicle ids today.
 *
 * It is to be REPLACED in Phase 2 by an Ops-side external-reference / metadata
 * field on the route version + shift solution (PRD GAP J / §8.4-B), at which
 * point the mapping round-trips through a backend-persisted+echoed field and
 * this module becomes a transient editing buffer only. Do NOT build new
 * process-critical identity state on top of this without that backend carrier.
 *
 * The store is intentionally tiny, dependency-free, and SSR/test-safe (guards
 * `localStorage` access). All reads degrade gracefully to an empty mapping.
 */

import type { Environment } from '../environmentConfig';

/** Schema version — bump if the persisted shape changes incompatibly. */
const STORE_VERSION = 1;

/** localStorage key prefix; the env:network composite is appended. */
const STORE_KEY_PREFIX = 'atlas_fixedRouteVehicleMapping';

/**
 * The persisted mapping: an Atlas-side identity (a vehicle id or a slot
 * `localId`) → the Ops `opsVehicleId` it is mapped to. Empty-string values are
 * treated as "not mapped" and pruned on write.
 */
export type VehicleMapping = Record<string, string>;

interface PersistedEnvelope {
  version: number;
  /** Echoed back for debugging / future migrations. */
  environment: Environment;
  networkId: string;
  mapping: VehicleMapping;
  /** ISO wall-clock timestamp of the last write (audit aid only). */
  updatedAt: string;
}

/** True when a usable `window.localStorage` is present (browser, not SSR/jsdom-less). */
function hasLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

/**
 * Builds the composite storage key for an `(environment, networkId)` pair.
 * Exported so tests (and any future migration tooling) can assert key shape.
 */
export function buildVehicleMappingKey(environment: Environment, networkId: string): string {
  return `${STORE_KEY_PREFIX}:${environment}:${networkId}`;
}

/** Drops empty / falsy targets so the stored mapping only holds real assignments. */
function pruneMapping(mapping: VehicleMapping): VehicleMapping {
  const next: VehicleMapping = {};
  for (const [key, value] of Object.entries(mapping)) {
    if (key && typeof value === 'string' && value.trim()) {
      next[key] = value;
    }
  }
  return next;
}

/**
 * Reads the persisted mapping for an `(environment, networkId)` pair. Returns an
 * empty object when nothing is stored, the entry is malformed, or storage is
 * unavailable — callers never have to handle a throw.
 */
export function getVehicleMapping(environment: Environment, networkId: string): VehicleMapping {
  if (!networkId || !hasLocalStorage()) return {};
  try {
    const raw = window.localStorage.getItem(buildVehicleMappingKey(environment, networkId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<PersistedEnvelope>;
    if (!parsed || typeof parsed !== 'object' || !parsed.mapping || typeof parsed.mapping !== 'object') {
      return {};
    }
    // Forward-compatible: unknown future versions are ignored rather than trusted.
    if (parsed.version !== STORE_VERSION) return {};
    return pruneMapping(parsed.mapping as VehicleMapping);
  } catch {
    return {};
  }
}

/**
 * Persists the mapping for an `(environment, networkId)` pair. Empty targets are
 * pruned; if the resulting mapping is empty the entry is cleared instead of
 * writing an empty envelope. No-ops (without throwing) when storage is
 * unavailable.
 */
export function setVehicleMapping(
  environment: Environment,
  networkId: string,
  mapping: VehicleMapping,
): void {
  if (!networkId || !hasLocalStorage()) return;
  const pruned = pruneMapping(mapping);
  const key = buildVehicleMappingKey(environment, networkId);
  try {
    if (Object.keys(pruned).length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    const envelope: PersistedEnvelope = {
      version: STORE_VERSION,
      environment,
      networkId,
      mapping: pruned,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(key, JSON.stringify(envelope));
  } catch {
    // Quota / serialization failures are non-fatal — the mapping simply does
    // not persist this session. The in-memory panel state is unaffected.
  }
}

/** Removes the persisted mapping for an `(environment, networkId)` pair. */
export function clearVehicleMapping(environment: Environment, networkId: string): void {
  if (!networkId || !hasLocalStorage()) return;
  try {
    window.localStorage.removeItem(buildVehicleMappingKey(environment, networkId));
  } catch {
    // ignore
  }
}
